import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cpu, Lightbulb, Sparkles, Zap, GraduationCap, Tag, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageLayout } from '@/components/layout/PageLayout';
import { EmbeddedSkillRail } from '@/components/skill';
import {
  contextualIntelligenceService,
  type ContextualLearningStats,
  type ContextualSnapshot,
  type ContextualSuggestion,
} from '../services/contextualIntelligence';
import { tabService, type Insight } from '../services/tabs';
import { resolveWorkspaceSessionId } from '@/services/workspaceSession';
import { executeContextualSuggestion, navigateForInsight } from '../utils/contextualActions';
import {
  deriveContextualInsights,
  mergeInsights,
  mergeServerInsights,
} from '../utils/contextualInsights';
import { derivePatternsFromTabs } from '../utils/tabPatterns';

type TabKey = 'suggestions' | 'insights';

const SUGGESTION_TYPE_BG: Record<ContextualSuggestion['type'], string> = {
  link: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  insight: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  action: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  resource: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

const CONFIDENCE_BAR: Record<'green' | 'blue' | 'yellow' | 'gray', string> = {
  green: 'bg-green-500',
  blue: 'bg-blue-500',
  yellow: 'bg-yellow-500',
  gray: 'bg-khiimori-text-muted',
};

const CONFIDENCE_TEXT: Record<'green' | 'blue' | 'yellow' | 'gray', string> = {
  green: 'text-green-600 dark:text-green-400',
  blue: 'text-blue-600 dark:text-blue-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  gray: 'text-khiimori-text-muted',
};

function getConfidenceTone(confidence: number): keyof typeof CONFIDENCE_BAR {
  if (confidence >= 90) return 'green';
  if (confidence >= 75) return 'blue';
  if (confidence >= 60) return 'yellow';
  return 'gray';
}

const IMPACT_BG: Record<'red' | 'yellow' | 'blue' | 'gray', string> = {
  red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  gray: 'bg-khiimori-bg-elevated text-khiimori-text-secondary',
};

interface InsightView {
  id: string;
  title: string;
  description: string;
  impactLabel: string;
  impactTone: keyof typeof IMPACT_BG;
  icon: string;
}

const INSIGHT_TYPE_ICON: Record<Insight['type'], string> = {
  suggestion: '💡',
  warning: '⚠️',
  opportunity: '✨',
  pattern: '🔄',
};

function mapInsightView(insight: Insight): InsightView {
  let impactTone: InsightView['impactTone'] = 'blue';
  let impactLabel = 'low';
  if (insight.type === 'warning') {
    impactTone = 'red';
    impactLabel = 'high';
  } else if (insight.confidence >= 80) {
    impactTone = 'yellow';
    impactLabel = 'medium';
  }
  return {
    id: insight.id,
    title: insight.title,
    description: insight.description,
    impactLabel,
    impactTone,
    icon: INSIGHT_TYPE_ICON[insight.type] ?? '💡',
  };
}

function formatLearningSince(iso?: string): string {
  if (!iso) return 'Getting started';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Getting started';
  if (d.getFullYear() < 1000) return 'Getting started';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

const ContextualIntelligence: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('suggestions');
  const [smartContext, setSmartContext] = useState<ContextualSnapshot | null>(null);
  const [learningStats, setLearningStats] = useState<ContextualLearningStats | null>(null);
  const [suggestions, setSuggestions] = useState<ContextualSuggestion[]>([]);
  const [insights, setInsights] = useState<InsightView[]>([]);
  const [derivedPatternCount, setDerivedPatternCount] = useState(0);
  const [proactiveSummary, setProactiveSummary] = useState<{
    activeSuggestions: number;
    patternCount: number;
    lastAnalysisAt?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchAll = useCallback(async (opts?: { silent?: boolean }) => {
    setIsLoading(true);
    try {
      const sessionId = await resolveWorkspaceSessionId();
      const [ctxRes, sugRes, proSuggRes, tabsRes, summaryRes, serverInsightsRes] = await Promise.allSettled([
        contextualIntelligenceService.getCurrentContext(),
        contextualIntelligenceService.getSuggestions(),
        tabService.getProactiveSuggestions(),
        sessionId
          ? tabService.getTabsBySession(sessionId, 1, 100)
          : Promise.resolve({ tabs: [], total: 0 }),
        tabService.getProactiveInsightSummary(),
        contextualIntelligenceService.getInsights(),
      ]);

      if (ctxRes.status === 'fulfilled') {
        setSmartContext(ctxRes.value.context);
        setLearningStats(ctxRes.value.learningStats);
      } else {
        setSmartContext(null);
        setLearningStats(null);
      }

      let initialSuggestions: ContextualSuggestion[] = [];
      if (sugRes.status === 'fulfilled') {
        initialSuggestions = sugRes.value;
        setSuggestions(initialSuggestions);
      } else {
        setSuggestions([]);
      }

      const tabs =
        tabsRes.status === 'fulfilled'
          ? tabsRes.value.tabs.map((tab) => ({
              url: tab.url,
              title: tab.title,
              updated_at: tab.updated_at,
            }))
          : [];
      const context = ctxRes.status === 'fulfilled' ? ctxRes.value.context : null;
      const suggestionRows = initialSuggestions;
      const derived = deriveContextualInsights(context, tabs, suggestionRows);

      // Prefer server-side insights from ProactiveIntelligenceEngine when
      // they're non-empty — that's the source of truth now. Fall back to the
      // client-only merger when the server is empty.
      const serverInsights =
        serverInsightsRes.status === 'fulfilled' ? serverInsightsRes.value : null;
      const apiInsights = proSuggRes.status === 'fulfilled' ? proSuggRes.value : [];
      const finalInsights = serverInsights
        ? mergeServerInsights(apiInsights, derived, serverInsights)
        : mergeInsights(apiInsights, derived);
      setInsights(finalInsights.map(mapInsightView));
      setDerivedPatternCount(derivePatternsFromTabs(tabs).length);

      if (summaryRes.status === 'fulfilled') {
        setProactiveSummary(summaryRes.value);
      } else {
        setProactiveSummary(null);
      }

      // Kick off LLM enrichment for the suggestion cards once we have them.
      // Result updates state in-place; subsequent re-fetches won't double up
      // thanks to the `enrichInFlight` ref guard.
      if (initialSuggestions.length > 0) {
        void enrichInBackground(initialSuggestions);
      }

      if (!opts?.silent) {
        const failed = [ctxRes, sugRes, proSuggRes, tabsRes, summaryRes].filter(
          (r) => r.status === 'rejected',
        );
        if (failed.length === 5) {
          toast.error('Failed to load contextual intelligence data');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fire-once-per-list LLM enrichment guard. We track the last-enriched ID set
  // so re-renders (e.g. from React strict-mode double-mount in dev) don't
  // trigger duplicate Gemini batches.
  const enrichInFlight = useRef(false);
  const lastEnrichedKey = useRef('');
  const enrichInBackground = useCallback(async (items: ContextualSuggestion[]) => {
    if (items.length === 0) return;
    const key = items.map((s) => s.id).sort().join(',');
    if (key === lastEnrichedKey.current) return;
    if (enrichInFlight.current) return;
    enrichInFlight.current = true;
    lastEnrichedKey.current = key;
    try {
      const enriched = await contextualIntelligenceService.enrichSuggestions(items);
      if (enriched.length === 0) return;
      // Merge: prefer enriched fields when they changed something; keep the
      // existing card if enrichment returned an empty title.
      const byId = new Map(enriched.map((s) => [s.id, s]));
      setSuggestions((prev) =>
        prev.map((s) => {
          const hit = byId.get(s.id);
          if (!hit || !hit.title) return s;
          return { ...s, title: hit.title, description: hit.description };
        }),
      );
    } catch {
      // Best-effort: keep the heuristic copy.
    } finally {
      enrichInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleFeedback = async (suggestionId: string, accepted: boolean) => {
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    setBusyId(suggestionId);
    const previous = suggestions;
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
    try {
      if (accepted && suggestion) {
        const actionMessage = await executeContextualSuggestion(suggestion, navigate);
        toast.success(actionMessage ?? 'Suggestion applied');
      } else {
        toast.success('Suggestion dismissed');
      }
      await contextualIntelligenceService.submitFeedback(suggestionId, accepted);
      void fetchAll({ silent: true });
    } catch (err) {
      console.error('feedback failed', err);
      setSuggestions(previous);
      toast.error('Could not complete that action');
    } finally {
      setBusyId(null);
    }
  };

  const handleInsightAction = async (insight: InsightView) => {
    setBusyId(insight.id);
    const previous = insights;
    setInsights((prev) => prev.filter((row) => row.id !== insight.id));
    try {
      const raw: Insight = {
        id: insight.id,
        type:
          insight.impactTone === 'red'
            ? 'warning'
            : insight.icon === '🔄'
              ? 'pattern'
              : 'suggestion',
        title: insight.title,
        description: insight.description,
        confidence: insight.impactTone === 'yellow' ? 85 : 70,
        timestamp: new Date().toISOString(),
      };
      toast.success(navigateForInsight(raw, navigate));
      try {
        await tabService.provideSuggestionFeedback(insight.id, 'accepted');
      } catch {
        // Proactive IDs may not exist for contextual-only insights.
      }
      void fetchAll({ silent: true });
    } catch (err) {
      console.error('insight action failed', err);
      setInsights(previous);
      toast.error('Could not act on that insight');
    } finally {
      setBusyId(null);
    }
  };

  const handleInsightDismiss = async (insightId: string) => {
    setBusyId(insightId);
    const previous = insights;
    setInsights((prev) => prev.filter((row) => row.id !== insightId));
    try {
      try {
        await tabService.provideSuggestionFeedback(insightId, 'rejected');
      } catch {
        await contextualIntelligenceService.submitFeedback(insightId, false);
      }
      toast.success('Insight dismissed');
      void fetchAll({ silent: true });
    } catch (err) {
      console.error('insight dismiss failed', err);
      setInsights(previous);
      toast.error('Could not dismiss that insight');
    } finally {
      setBusyId(null);
    }
  };

  const runBehaviorAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      await tabService.analyzeUserBehavior(30);
      toast.success('Behavior analysis started');
      await fetchAll({ silent: true });
    } catch (err) {
      console.error('behavior analysis failed', err);
      toast.error('Could not run behavior analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const accuracyText = useMemo(() => {
    const rate = learningStats?.acceptanceRate ?? 0;
    if (!learningStats || learningStats.feedbackCount === 0) return '—';
    return `${Math.round(rate * 100)}%`;
  }, [learningStats]);

  const sessionsAnalyzed = learningStats?.snapshotCount ?? 0;
  const patternsLearned = Math.max(proactiveSummary?.patternCount ?? 0, derivedPatternCount);
  const lastLearningSince = formatLearningSince(
    proactiveSummary?.lastAnalysisAt ?? learningStats?.firstSnapshotAt,
  );

  return (
    <PageLayout
      title="Contextual Intelligence"
      description="AI-powered contextual awareness and intelligent suggestions for your workflow"
    >
      <EmbeddedSkillRail
        cardClassName="mb-6"
        title="Context Skills"
        description="Skills for analyzing, mapping, and organising tab context."
        skillIds={['analyze_workspace', 'build_relationship_map', 'auto_organize', 'smart_merge', 'smart_prioritize']}
      />
      {/* Smart Context Card */}
      <Card className="bg-[var(--khiimori-bg-tertiary)] border-[var(--khiimori-border)]">
        <div className="flex items-start space-x-4">
          <div
            className="p-3 rounded-lg flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, var(--khiimori-accent-violet), var(--khiimori-accent-cyan))' }}
          >
            <Cpu className="h-8 w-8 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-[var(--khiimori-text-primary)]">Current Context</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void fetchAll({ silent: true })}
                  disabled={isLoading}
                  leftIcon={<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
                >
                  Refresh
                </Button>
                <div
                  className="px-3 py-1 rounded-lg text-sm font-medium flex items-center space-x-2"
                  style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}
                >
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#a78bfa' }} />
                  <span>{isLoading ? 'Analyzing…' : 'AI Active'}</span>
                </div>
              </div>
            </div>
            {!smartContext && !isLoading ? (
              <p className="text-sm text-[var(--khiimori-text-muted)]">
                No context detected yet. Browse a few tabs to teach the AI about your workflow.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ContextRow
                  icon={<Zap className="h-5 w-5 mt-1" style={{ color: 'var(--khiimori-accent-violet)' }} />}
                  label="Activity"
                  value={smartContext?.activity ?? '—'}
                />
                <ContextRow
                  icon={<Lightbulb className="h-5 w-5 mt-1" style={{ color: 'var(--khiimori-accent-cyan)' }} />}
                  label="Detected Intent"
                  value={smartContext?.detectedIntent || 'Listening for cues…'}
                />
                <ContextRow
                  icon={<Clock className="h-5 w-5 mt-1" style={{ color: 'var(--khiimori-accent-amber)' }} />}
                  label="Time Context"
                  value={smartContext?.timeContext ?? '—'}
                />
                <div className="flex items-start space-x-3">
                  <Tag className="h-5 w-5 mt-1" style={{ color: 'var(--khiimori-accent-fuchsia)' }} />
                  <div>
                    <div className="text-sm text-[var(--khiimori-text-muted)]">Related Topics</div>
                    {smartContext && smartContext.relatedTopics.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {smartContext.relatedTopics.map((topic, idx) => (
                          <span
                            key={`${topic}-${idx}`}
                            className="px-2 py-0.5 rounded text-xs"
                            style={{ background: 'rgba(139,92,246,0.2)', color: '#c4b5fd' }}
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-[var(--khiimori-text-muted)] mt-1">
                        No topics detected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="flex space-x-2 mb-2">
        {(
          [
            { id: 'suggestions', label: 'Smart Suggestions', icon: Sparkles },
            { id: 'insights', label: 'Insights', icon: GraduationCap },
          ] as const
        ).map((tab) => {
          const Icon = tab.icon;
          return (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'primary' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              leftIcon={<Icon className="h-5 w-5" />}
            >
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* Suggestions tab */}
      {activeTab === 'suggestions' && (
        <SuggestionsTab
          suggestions={suggestions}
          isLoading={isLoading}
          busyId={busyId}
          onApply={(id) => handleFeedback(id, true)}
          onDismiss={(id) => handleFeedback(id, false)}
        />
      )}

      {activeTab === 'insights' && (
        <InsightsTab
          insights={insights}
          isLoading={isLoading}
          busyId={busyId}
          onAction={(insight) => void handleInsightAction(insight)}
          onDismiss={(id) => void handleInsightDismiss(id)}
          onRunAnalysis={() => void runBehaviorAnalysis()}
          isAnalyzing={isAnalyzing}
        />
      )}

      {/* AI Learning Status */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-[var(--khiimori-bg-tertiary)] border-[var(--khiimori-border)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="p-3 rounded-lg flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, var(--khiimori-accent-violet), var(--khiimori-accent-cyan))' }}
              >
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--khiimori-text-primary)] mb-1">AI Learning Progress</h3>
                <p className="text-sm text-[var(--khiimori-text-muted)]">
                  Analyzed <strong>{sessionsAnalyzed.toLocaleString()}</strong> context snapshots and learned{' '}
                  <strong>{patternsLearned}</strong> behaviour patterns. Active for {lastLearningSince}.
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {accuracyText}
              </div>
              <div className="text-xs text-[var(--khiimori-text-muted)]">Acceptance rate</div>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

interface ContextRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function ContextRow({ icon, label, value }: ContextRowProps) {
  return (
    <div className="flex items-start space-x-3">
      {icon}
      <div>
        <div className="text-sm text-khiimori-text-muted">{label}</div>
        <div className="font-semibold text-khiimori-text-primary">{value}</div>
      </div>
    </div>
  );
}

interface SuggestionsTabProps {
  suggestions: ContextualSuggestion[];
  isLoading: boolean;
  busyId: string | null;
  onApply: (id: string) => void;
  onDismiss: (id: string) => void;
}

function SuggestionsTab({ suggestions, isLoading, busyId, onApply, onDismiss }: SuggestionsTabProps) {
  if (isLoading && suggestions.length === 0) {
    return (
      <Card className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </Card>
    );
  }
  if (suggestions.length === 0) {
    return (
      <Card className="p-6 text-sm text-khiimori-text-muted">
        No suggestions yet. As the AI learns from your activity, contextual recommendations will appear here.
      </Card>
    );
  }
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <AnimatePresence mode="popLayout">
        {suggestions.map((suggestion, index) => {
          const tone = getConfidenceTone(suggestion.confidence);
          const isBusy = busyId === suggestion.id;
          return (
            <motion.div
              key={suggestion.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card hoverable>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="text-4xl">{suggestion.icon || '💡'}</div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-khiimori-text-primary">
                          {suggestion.title}
                        </h3>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${SUGGESTION_TYPE_BG[suggestion.type]}`}
                        >
                          {suggestion.type}
                        </span>
                      </div>
                      <p className="text-khiimori-text-muted mb-3">{suggestion.description}</p>
                      <div className="flex items-center space-x-4 flex-wrap gap-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-khiimori-text-muted">Confidence:</div>
                          <div className="flex-1 h-2 w-24 bg-khiimori-bg-elevated/5 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${CONFIDENCE_BAR[tone]}`}
                              style={{ width: `${suggestion.confidence}%` }}
                            />
                          </div>
                          <span className={`text-sm font-semibold ${CONFIDENCE_TEXT[tone]}`}>
                            {suggestion.confidence}%
                          </span>
                        </div>
                        <div className="flex space-x-1 flex-wrap gap-y-1">
                          {suggestion.tags.map((tag, idx) => (
                            <span
                              key={`${tag}-${idx}`}
                              className="px-2 py-1 bg-khiimori-bg-elevated/5 text-khiimori-text-secondary rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onApply(suggestion.id)}
                      disabled={isBusy}
                      isLoading={isBusy}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onDismiss(suggestion.id)}
                      disabled={isBusy}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
}

interface InsightsTabProps {
  insights: InsightView[];
  isLoading: boolean;
  busyId: string | null;
  onAction: (insight: InsightView) => void;
  onDismiss: (id: string) => void;
  onRunAnalysis: () => void;
  isAnalyzing: boolean;
}

function InsightsTab({
  insights,
  isLoading,
  busyId,
  onAction,
  onDismiss,
  onRunAnalysis,
  isAnalyzing,
}: InsightsTabProps) {
  if (isLoading && insights.length === 0) {
    return (
      <Card className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </Card>
    );
  }
  if (insights.length === 0) {
    return (
      <Card className="p-6 space-y-4">
        <p className="text-sm text-khiimori-text-muted">
          No insights yet. Open a few tabs or refresh context to surface live session insights.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={onRunAnalysis} isLoading={isAnalyzing}>
            Analyze behavior
          </Button>
          <Link to="/ai?section=patterns">
            <Button variant="secondary">Open Patterns tab</Button>
          </Link>
        </div>
      </Card>
    );
  }
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      {insights.map((insight, index) => {
        const isBusy = busyId === insight.id;
        return (
          <motion.div
            key={insight.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card hoverable>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="text-4xl">{insight.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-khiimori-text-primary">{insight.title}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${IMPACT_BG[insight.impactTone]}`}
                      >
                        {insight.impactLabel} impact
                      </span>
                    </div>
                    <p className="text-khiimori-text-muted">{insight.description}</p>
                  </div>
                </div>
                <div className="flex space-x-2 shrink-0">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onAction(insight)}
                    disabled={isBusy}
                    isLoading={isBusy}
                  >
                    Act
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => onDismiss(insight.id)} disabled={isBusy}>
                    Dismiss
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

export default ContextualIntelligence;
